
import { useState, useEffect, useReducer } from "react";
import { Box, Card, CardContent, Divider, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody, Stack, InputAdornment, Tooltip, IconButton } from "@mui/material";
import { z } from "zod";

import useFetch 		from "../../api/api";
import config 			from "../../common/Config";

import HelpOutlineIcon 	from "@mui/icons-material/HelpOutline";
import WarningIcon 		from '@mui/icons-material/Warning';
import { formatCurrency } from "../../common/Helper";

const DiscountCashFlow = ({company, stockPrice, exchangeRate, inflation}) => {

	const privateFetch 								= useFetch()

	const [inputFields, dispatch]					= useReducer(reducer, { 
		lastFcf				: 0,
		avgGrowth			: 0,
		discountRate		: 9,
		terminalGrowth		: 3,
		numShares			: 1000,
		projYears			: 10,
		currency			: 'USD',
		isValid				: false,
		errors				: {}
	}) 
	
	const [equityValue, setEquityValue] 			= useState(null);
	const [pricePerShare, setPricePerShare] 		= useState(null);
	const [projections, setProjections] 			= useState([]);

	useEffect(() => {
		if (company) calculateInitialData();
	}, [company]);

	useEffect(() => {
		if (inputFields.isValid) calculateDCF(false);
	}, [inputFields]);

	const calculateInitialData = async () => {
		const fieldsToUpdate = [];

		if (!company?.financialReports?.length) return;

		// Se a companhia esta nestes setores, não recomenda o uso de DCF
		if (['Financeiro','Financeiro e Outros','Financial Services'].includes(company.sector)) {
			fieldsToUpdate.push({type: 'boolean', field: 'usageWarning', payload: true })
		}

		const financialReports 	= [...company.financialReports].sort((a, b) => a.year - b.year);
		const computedFcf 		= financialReports.map((fr) => fr.fco - Math.abs(fr.capex));

		var last				 	= computedFcf.at(-1) || 0;
		var currentExchangeRate 	= 1
		var exchangeMessage			= ''

		if (company.country !== 'BR' && company.country !== 'US') {
			fieldsToUpdate.push({ type: 'string', field: 'lastFcfIcon', payload: 'warning'})
			currentExchangeRate = exchangeRate
			exchangeMessage		= 'Valores convertidos para USD. '
		}
		
		last = Math.round(last * currentExchangeRate)

		if ( last > 0 ) {
			fieldsToUpdate.push({ type: 'string', field: 'lastFcfMessage', payload: 'FCF último ano calculado como FCO - CAPEX. '+ exchangeMessage });
		}
		else {
			fieldsToUpdate.push({ type: 'string', field: 'lastFcfIcon', payload: 'warning'})
			fieldsToUpdate.push({ type: 'string', field: 'lastFcfMessage', payload: 'FCF último ano calculado como FCO - CAPEX negativo. Valor deve ser ajustado manualmente. '+ exchangeMessage});
		}

		fieldsToUpdate.push({ type: 'number', field: 'lastFcf', payload: last});
		
		const avg = Math.pow(computedFcf[computedFcf.length - 1]/computedFcf[0], 1/(computedFcf.length-1)) - 1
		fieldsToUpdate.push({ type: 'number', field: 'avgGrowth', payload: inflation });
		fieldsToUpdate.push({ type: 'string', field: 'avgGrowthMessage', payload: "Taxa de crescimento definida como inflação do último periodo. Outros valores para referência: <br/><br/> CAGR do crescimento do FCF: "+ parseFloat(avg * 100).toFixed(2)});
		
		
		// Ajusta da taxa de desconto e cresicemento se o pais for Brasil
		if (company.country === 'BR') {
			fieldsToUpdate.push({ type: 'number', field: 'discountRate', payload: 13 });
			fieldsToUpdate.push({ type: 'number', field: 'terminalGrowth', payload: 2 });
			fieldsToUpdate.push({ type: 'string', field: 'currency', payload: 'BRL' });
		}

		fieldsToUpdate.push({type: 'number', field : 'numShares', payload: Math.round(financialReports.at(-1).netIncome / financialReports.at(-1).eps) || 1})		

		dispatch({type: 'batchUpdate', payload: fieldsToUpdate});
	}

	const calculateDCF = async () => {
		const n 				= inputFields.projYears;
		const projectionsArr 	= [];
		let fcfProj 			= inputFields.lastFcf;
		let pv 					= 0;

		for (let i = 1; i <= n; i++) {
			fcfProj = fcfProj * (1 + (inputFields.avgGrowth/100));
			const discounted = fcfProj / Math.pow(1 + (inputFields.discountRate/100), i);
			pv += discounted;
			projectionsArr.push({
				ano	: i,
				fcf	: fcfProj,
				pv	: discounted
			});
		}

		const terminalValue = (fcfProj * (1 + (inputFields.terminalGrowth/100))) / ((inputFields.discountRate/100) - (inputFields.terminalGrowth/100));
		const pvTerminal = terminalValue / Math.pow(1 + (inputFields.discountRate/100), n);
		pv += pvTerminal;

		const totalEquity = pv;
		setEquityValue(totalEquity);
		setPricePerShare(totalEquity / inputFields.numShares);
		setProjections([...projectionsArr, { ano: "Terminal", fcf: terminalValue, pv: pvTerminal }]);
	};

	return (
		<Card>
			<CardContent>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Discounted Cash Flow
					{inputFields.usageWarning && (
						<Tooltip title="O modelo DCF não é recomendado para bancos e instituições financeiras porque o fluxo de caixa livre (FCO – CAPEX) inclui entradas e saídas de depósitos, empréstimos e resgates, que são parte da atividade principal, não fluxos de geração de valor, além de utilizar o endividamento como matéria-prima do negócio." componentsProps={{tooltip: {sx: {fontSize: 12}}}}>
							<IconButton size="medium"><WarningIcon  fontSize="medium" sx={{ color: 'red' }} /></IconButton>
						</Tooltip>
					)}
				</Typography>
				
				<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
					<Box sx={{ flex: 1 }}>

						<TextField
							label="FCF último ano (mi)"
							type="number"
							value={inputFields.lastFcf}
							onChange={(e) => dispatch({type: 'update', field : 'lastFcf', payload: e.target.value})}
							error={inputFields.errors?.lastFcf}
							helperText={inputFields.errors?.lastFcf}								
							fullWidth
							sx={{ mb: 1 }}
							InputProps={{
								endAdornment: (
								<InputAdornment position="end">
									<Tooltip title={inputFields.lastFcfMessage} componentsProps={{tooltip: {sx: {fontSize: 12}}}}>
										<IconButton size="medium">
											{inputFields.lastFcfIcon === 'warning' ? (<WarningIcon  fontSize="medium" sx={{ color: 'red' }} />) : (<HelpOutlineIcon fontSize="medium" />) }
										</IconButton>
									</Tooltip>
								</InputAdornment>
								),
							}}	
						/>
						<TextField
							label="Taxa média de crescimento do FCF (%)"
							type="number"
							value={inputFields.avgGrowth}
							onChange={(e) => dispatch({type: 'update', field : 'avgGrowth', payload: e.target.value})}
							error={inputFields.errors?.avgGrowth}
							helperText={inputFields.errors?.avgGrowth}								
							fullWidth
							sx={{ mb: 1 }}
							InputProps={{
								endAdornment: (
								<InputAdornment position="end">
									<Tooltip title={inputFields.avgGrowthMessage} componentsProps={{tooltip: {sx: {fontSize: 12}}}}>
										<IconButton size="medium">
											{inputFields.avgGrowthIcon === 'info' ? (<HelpOutlineIcon fontSize="medium" />) : (<WarningIcon  fontSize="medium" sx={{ color: 'red' }} />)}
										</IconButton>
									</Tooltip>
								</InputAdornment>
								),
							}}								
						/>							
						<TextField
							label="Anos de projeção"
							type="number"
							value={inputFields.projYears}
							onChange={(e) => dispatch({type: 'update', field : 'projYears', payload: e.target.value})}
							error={inputFields.errors?.projYears}
							helperText={inputFields.errors?.projYears}								
							fullWidth
							sx={{ mb: 1 }}
							inputProps={{ min: 1, max: 10 }}
						/>
						<TextField
							label="Taxa de desconto (%)"
							type="number"
							value={inputFields.discountRate}
							onChange={(e) => dispatch({type: 'update', field : 'discountRate', payload: e.target.value})}
							error={inputFields.errors?.discountRate}
							helperText={inputFields.errors?.discountRate}								
							fullWidth
							sx={{ mb: 1 }}
						/>
						<TextField
							label="Crescimento perpétuo (%)"
							type="number"
							value={inputFields.terminalGrowth}
							onChange={(e) => dispatch({type: 'update', field : 'terminalGrowth', payload: e.target.value})}
							error={inputFields.errors?.terminalGrowth}
							helperText={inputFields.errors?.terminalGrowth}								
							fullWidth
							sx={{ mb: 1 }}
						/>

						<TextField
							label="Número de ações (mi)"
							type="number"
							value={inputFields.numShares}
							onChange={(e) => dispatch({type: 'update', field : 'numShares', payload: e.target.value})}
							error={inputFields.errors?.numShares}
							helperText={inputFields.errors?.numShares}
							fullWidth
							sx={{ mb: 1 }}
						/>

					</Box>

					<Divider sx={{ mx: 2 }} orientation={{ xs: "horizontal", xl: "vertical" }} flexItem/>

					<Box sx={{ flex: 1 }}>
						<Table size="small">
							<TableHead>
							<TableRow>
								<TableCell>Ano</TableCell>
								<TableCell align="right">FCF projetado (mi)</TableCell>
								<TableCell align="right">Valor Presente (mi)</TableCell>
							</TableRow>
							</TableHead>
							<TableBody>
							{projections.map((p, idx) => (
								<TableRow key={idx}>
									<TableCell>{p.ano}</TableCell>
									<TableCell align="right">{formatCurrency(p.fcf, 'BIG', inputFields.currency)}</TableCell>
									<TableCell align="right">{formatCurrency(p.pv, 'BIG', inputFields.currency)}</TableCell>
								</TableRow>
							))}
							</TableBody>
						</Table>
					</Box>
				</Stack>

				{pricePerShare !== null && (
					<Box mt={2}>
						<Typography variant="h6">Equity Value Total (PV): {equityValue > 0 ? formatCurrency(equityValue, 'BIG', inputFields.currency) +" milhões" : "Indeterminado"} </Typography>

						<Box
							mt={2}
							p={2}
							sx={{
							backgroundColor: stockPrice < pricePerShare ? "#e8f5e9" : "#ffebee",
							borderRadius: 2,
							textAlign: "center",
							border: "2px solid "+ (stockPrice < pricePerShare ? "#2e7d32" : "#c62828")
							}}
							>
							<Typography variant="h5" sx={{ color: (stockPrice < pricePerShare ? "#2e7d32" : "#c62828"), fontWeight: "bold" }}>
							💰 Preço justo por ação: {pricePerShare > 0 ? formatCurrency(pricePerShare, 'SMALL', inputFields.currency) : 'Indeterminado'}
							</Typography>
						</Box>
					</Box>
				)}

			</CardContent>

		</Card>
	);
};

const reducer = (inputFields, action) => {
	let updatedFields = {...inputFields};

	switch (action.type) {

		case 'update' : {
			updatedFields = {
				...updatedFields,
				[action.field] : parseFloat(action.payload)
			};
			break;
		}

		case 'batchUpdate' : {
			action.payload.forEach( (update) => {
				switch (update.type) {
					case 'number' :
						update.payload = parseFloat(update.payload);
						break;
					case 'string' :
						update.payload = update.payload.toString();
						break;
					case 'boolean' : 
						update.payload = typeof update.payload === 'string' ? update.payload.toLowerCase() === 'true' : Boolean(update.payload);				
				}

				updatedFields = {
					...updatedFields,
					[update.field] : update.payload
				}
			});
			break;
		}
	}

	const zodParseResults = inputFieldsSchema.safeParse(updatedFields)

	updatedFields.isValid 	= zodParseResults.success
	updatedFields.errors	= parseZodErrors(zodParseResults)

	return updatedFields
}

const inputFieldsSchema = z.object({
	lastFcf			: z.number({message: 'FCF precisa ser numérico'}).min(0, {message: 'FCF inválido!'}),
	avgGrowth		: z.number({message: 'Taxa de crescimento precisa ser numérica'}).min(0, {message: 'Crescimento inválido!'}).max(100, {message: 'Crescimento máximo de 100%!'}),
	discountRate	: z.number({message: 'Taxa de desconto precisa ser numérica'}).min(0.1, {message: 'Taxa de desconto inválida!'}).max(100, {message: 'Taxa de desconto máxima de 100%!'}),
	terminalGrowth	: z.number({message: 'Taxa de crescimento perpétuo precisa ser numérica'}).min(0, {message: 'Crescimento perpétuo inválido!'}).max(20, {message: 'Crescimento perpétuo máximo de 20%!'}),
	numShares		: z.number({message: 'Número de ações precisa ser numérico'}).min(1, {message: 'Número de ações inválido!'}),
	projYears		: z.number({message: 'Anos de projeção precisa ser numérico'}).int({message: 'Anos de projeção precisa ser um número inteiro'}).min(1, {message: 'Anos de projeção inválido!'}).max(10, {message: 'Máximo de 10 anos!'}),
})

const parseZodErrors = (pErrors) => {
	return pErrors.error?.issues?.reduce( (errors, e) => ({...errors, [e.path[0]] : e.message}), {})
}

export default DiscountCashFlow;
